import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "./LanguageContext";
import { IznikRosette, IznikTulip, IznikTexture } from "./Motifs";
import Petals from "./Petals";

const EASE = [0.76, 0, 0.24, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`reveal-mask ${className}`}>
      <motion.span
        className="inline-block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.2, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const yNames = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yMotif = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex flex-col justify-center"
    >
      {/* iznik texture */}
      <IznikTexture size={320} opacity={0.045} />

      {/* drifting petals */}
      <Petals />

      {/* layered motifs */}
      <motion.div
        style={{ y: yMotif }}
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
      >
        <IznikRosette className="absolute -top-16 -right-16 w-[380px] h-[380px] opacity-[0.10] rotate-12" />
        <IznikRosette className="absolute bottom-10 -left-24 w-[420px] h-[420px] opacity-[0.08]" />
        <IznikTulip className="absolute top-1/2 right-[8%] w-16 opacity-40 hidden md:block" />
        <IznikTulip className="absolute top-[26%] left-[9%] w-12 opacity-30 hidden md:block" />
      </motion.div>

      <motion.div
        style={{ y: yNames, opacity }}
        className="relative z-10 mx-auto w-full max-w-6xl px-6 md:px-10"
      >
        {/* top overline */}
        <div className="flex items-center gap-4 mb-6 md:mb-10">
          <MaskedLine delay={0.2}>
            <span className="flex items-center gap-3 font-sans text-xs md:text-sm uppercase tracking-[0.32em] text-turq">
              <span className="inline-block h-1.5 w-1.5 rotate-45 bg-gold" />
              {t.hero.kicker}
            </span>
          </MaskedLine>
          <motion.span
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: EASE }}
            className="h-px flex-1 origin-left bg-gold/50"
          />
        </div>

        {/* names */}
        <h1 className="font-display leading-[0.84] tracking-[-0.01em] text-ink">
          <MaskedLine delay={0.35} className="block">
            <span className="block text-[19vw] md:text-[13vw] lg:text-[11.5vw]">
              Nurhan
            </span>
          </MaskedLine>
          <MaskedLine delay={0.5} className="block">
            <span className="block text-[19vw] md:text-[13vw] lg:text-[11.5vw] pl-[0.6em] italic text-turq">
              <span className="text-gold not-italic font-light">&amp;</span> Emre
            </span>
          </MaskedLine>
        </h1>

        {/* bottom details */}
        <div className="mt-10 md:mt-14 pt-6 border-t border-gold/25 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <MaskedLine delay={0.9}>
            <p className="font-sans text-sm md:text-base tracking-wide text-ink/80">
              {t.hero.dateLine}
              <span className="mx-2 text-gold">·</span>
              {t.hero.timeLine}
            </p>
          </MaskedLine>
          <MaskedLine delay={1.0}>
            <p className="font-sans text-xs md:text-sm uppercase tracking-[0.22em] text-ink/50">
              {t.hero.place}
            </p>
          </MaskedLine>
        </div>
      </motion.div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.7 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-ink/50">
          {t.hero.scroll}
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="block h-8 w-px bg-gradient-to-b from-gold to-transparent"
        />
      </motion.div>
    </section>
  );
}
